import React from 'react';
import { css, cx } from '@emotion/css';
import { IconCaretLeft, IconCaretRight, expandFontTheme, theme, useThemeContext, getThemeColor, } from '@contrast-security-inc/design-system-foundations';
import { SelectedState, HoverState, isFirstDayOfTheWeek, isFirstDayOfTheMonth, isLastDayOfTheWeek, isLastDayOfTheMonth, } from './shared';
export const calendarBackButtonTestID = 'calendar-back-button-test-id';
export const calendarForwardButtonTestID = 'calendar-forward-button-test-id';
export const getUniqueDayKeyFromDate = (date) => `${date.getFullYear()}-${date.getMonth() + 1}-${date.getDate()}`;
const iconColor = (selectedTheme) => (selectedTheme === 'dark' ? 'neutral30' : 'neutral100');
export const Month = ({ children }) => {
    return (React.createElement("div", { className: css `
        flex: 1;
        display: flex;
        flex-flow: column nowrap;
        margin: 27px;
        text-align: center;
      ` }, children));
};
export const MonthHeader = ({ month, year }) => {
    const { selectedTheme } = useThemeContext();
    return (React.createElement("div", { className: css `
          display: flex;
          padding: 0 7px;
          justify-content: center;
          height: 28px;
          align-items: center;
          color: ${getThemeColor(selectedTheme, { light: theme.color.neutral100, dark: theme.color.neutral30 })};
        ` },
        React.createElement("span", { className: css `
            ${expandFontTheme(theme.font.bodyStrong)}
            align-self: flex-start;
          ` },
            month,
            " ",
            year)));
};
const dayCSS = css `
  ${expandFontTheme(theme.font.metadata)}
`;
export const DayHeader = ({ dayAbbreviations }) => {
    const { selectedTheme } = useThemeContext();
    return (React.createElement(React.Fragment, null,
        React.createElement("div", { className: css `
          display: flex;
          color: ${getThemeColor(selectedTheme, { light: theme.color.neutral60, dark: theme.color.neutral40 })};
        ` }, dayAbbreviations.map((day, index) => {
            return (React.createElement("span", { key: `day-${index}`, className: cx(dayCSS, css `
                  flex: 1;
                  padding: 5px 0;
                `) }, day));
        })),
        React.createElement("div", { className: css `
          margin: 0 11px 5px;
          height: 1px;
          background-color: ${theme.color.neutral30};
        ` })));
};
const dayButtonCSS = (selectable, selectedState, hoverState, date, selectedTheme) => css `
  display: flex;
  flex-flow: column;
  align-items: center;
  justify-content: center;
  flex: 1;
  position: relative;
  cursor: ${selectable ? 'pointer' : 'default'};
  padding: 0;
  background: unset;
  border: unset;

  &::before,
  &::after {
    content: "";
    width: 50%;
    height: 24px;
    position: absolute;
  }

  &::before {
    left: 0;
    background-color: ${!isFirstDayOfTheMonth(date) && !isFirstDayOfTheWeek(date)
    ? getLeftRangeColor(selectedState, hoverState, selectedTheme)
    : ''}
  }

  &::after {
    right: 0;
    background-color: ${!isLastDayOfTheWeek(date) && !isLastDayOfTheMonth(date)
    ? getRightRangeColor(selectedState, hoverState, selectedTheme)
    : ''}
  }
`;
const daySpanColor = (selectable, selectedState, selectedTheme) => {
    if (selectedTheme === 'dark') {
        return !selectable ? theme.color.neutral90 : theme.color.neutral10;
    }
    return !selectable ? theme.color.neutral50 : theme.color.neutral100;
};
const daySpanCss = (selectable, selectedState, hoverState, selectedTheme) => css `
  display: flex;
  flex-flow: column;
  justify-content: center;
  margin: 2px 0px;
  width: 24px;
  height: 24px;
  z-index: 2;
  border-radius: 4px;
  color: ${daySpanColor(selectable, selectedState, selectedTheme)};

  /* Dark theme hover using a darker teal70, probably what a teal90 would be if it existed */
  ${hoverState
    ? `background-color: ${getThemeColor(selectedTheme, { light: theme.color.teal10, dark: 'rgb(21, 69, 63)' })};`
    : ''}
  ${selectedState
    ? `color: ${theme.color.white};
         background-color: ${theme.color.turquoise70};`
    : ''}
  &:focus-visible {
    outline: 1px solid ${theme.color.turquoise70};
  }
`;
export const getLeftRangeColor = (selectedState, hoverState, selectedTheme) => {
    if (selectedState === SelectedState.SELECTED_BEGINNING_OF_SELECTION || selectedState === SelectedState.SELECTED) {
        return;
    }
    if ([SelectedState.SELECTED_WITHIN_SELECTION_RANGE, SelectedState.SELECTED_END_OF_SELECTION].includes(selectedState)) {
        return theme.color.turquoise70;
    }
    else if ([HoverState.HOVERED_WITHIN_HOVER_RANGE, HoverState.HOVERED_END_OF_HOVER_RANGE].includes(hoverState)) {
        return getThemeColor(selectedTheme, { light: theme.color.teal10, dark: 'rgb(21, 69, 63)' });
    }
};
export const getRightRangeColor = (selectedState, hoverState, selectedTheme) => {
    if (selectedState === SelectedState.SELECTED_BEGINNING_OF_SELECTION ||
        selectedState === SelectedState.SELECTED_WITHIN_SELECTION_RANGE) {
        return theme.color.turquoise70;
    }
    else if (hoverState === HoverState.HOVERED_WITHIN_HOVER_RANGE) {
        return getThemeColor(selectedTheme, { light: theme.color.teal10, dark: 'rgb(21, 69, 63)' });
    }
};
const handleKeyDown = (event, onClick) => {
    if (event.key === 'Enter') {
        onClick(event);
    }
};
export const Week = ({ week, getDateProps, getHoverState, getSelectedState, onDateMouseOver, onDateMouseOut, }) => {
    const { selectedTheme } = useThemeContext();
    return (React.createElement("div", { className: css `
        display: flex;
      ` }, week.map((dateObj, dayIndex) => {
        if (!dateObj || typeof dateObj === 'string') {
            return (React.createElement("div", { key: `empty-day-${dayIndex}`, className: css `
                flex: 1;
              ` }));
        }
        const { date, selectable } = dateObj;
        const dayKey = getUniqueDayKeyFromDate(date);
        const hoverState = getHoverState(dateObj);
        const selectedState = getSelectedState(dateObj);
        const dateProps = getDateProps({
            dateObj,
            className: cx(dayCSS, dayButtonCSS(selectable, selectedState, hoverState, date, selectedTheme)),
        });
        return (React.createElement("button", { tabIndex: selectedState ? 0 : -1, key: dayKey, ...dateProps, onMouseOver: () => onDateMouseOver?.(dateObj), onFocus: () => onDateMouseOver?.(dateObj), onMouseOut: () => onDateMouseOut?.(dateObj), onBlur: () => onDateMouseOut?.(dateObj), "data-testid": dayKey, "data-selectable": selectable, "data-hovered": !!hoverState && selectedState !== SelectedState.SELECTED_END_OF_SELECTION, "data-selected": !!selectedState, onKeyDown: (event) => handleKeyDown(event, dateProps.onClick), "aria-pressed": !!selectedState, type: "button" },
            React.createElement("span", { tabIndex: selectable ? 0 : -1, className: cx(daySpanCss(selectable, selectedState, hoverState, selectedTheme)) }, date.getDate())));
    })));
};
const CalendarButton = ({ getButtonProps, renderButton, className, ...props }) => {
    const buttonProps = getButtonProps({
        className: css `
      margin: 0;
      padding: 0;
      width: auto;
      overflow: visible;
      background: unset;
      height: 24px;
      cursor: pointer;
      line-height: normal;
      border: 1px solid ${theme.color.transparent};

      &:focus-visible {
        border-color: ${theme.color.turquoise70};
        border-radius: 6px;
        outline: unset;
      }
    `,
    });
    return (React.createElement("div", { className: className },
        React.createElement("button", { ...props, onKeyDown: (event) => handleKeyDown(event, buttonProps.onClick), ...buttonProps, type: "button" }, renderButton({ disabled: buttonProps.disabled }))));
};
export const CalendarBackButton = (props) => {
    const { selectedTheme } = useThemeContext();
    return (React.createElement(CalendarButton, { ...props, "data-testid": calendarBackButtonTestID, className: css `
        position: absolute;
        top: 29px;
        left: 35px;
      `, renderButton: ({ disabled }) => {
            return !disabled && React.createElement(IconCaretLeft, { color: iconColor(selectedTheme) });
        } }));
};
export const CalendarForwardButton = (props) => {
    const { selectedTheme } = useThemeContext();
    return (React.createElement(CalendarButton, { ...props, "data-testid": calendarForwardButtonTestID, className: css `
        position: absolute;
        top: 29px;
        right: 35px;
      `, renderButton: ({ disabled }) => {
            return !disabled && React.createElement(IconCaretRight, { color: iconColor(selectedTheme) });
        } }));
};
